import { DateTime } from 'luxon';
import { BaseApi } from './base';
import type {
  CardActionPayload,
  Recurrence,
  RecurrenceConfig,
  RecurrenceTaskItem,
  RecurrenceTaskStatus,
  User,
} from './types';

export { RecurrenceTaskStatusEnum } from './types';

export interface GetAllTasksUser {
  _id: string;
  email: string;
  given_name: string;
  family_name: string;
  picture: string;
}

export interface GetAllTasksResponse {
  recurrence: Recurrence;
  user: User;
  tasks: RecurrenceTaskItem[];
}

export interface CreateRecurrencePayload {
  title: string;
  description?: string;
  assignedTo: string[];
  rewardCard: string | CardActionPayload[];
  penaltyCard?: string | CardActionPayload[];
  icon: string;
  config: RecurrenceConfig;
}

export interface TaskApprovalRequest {
  _id: string;
  assignedTo: User;
  recurrence: Recurrence;
  status: 'CompleteRequest';
  createdAt: DateTime;
  updatedAt: DateTime;
}

export interface GetCurrentTasks {
  recurrence: Recurrence;
  tasks: RecurrenceTaskItem[];
}

export class TasksApi extends BaseApi {
  public async create(payload: CreateRecurrencePayload): Promise<void> {
    await this._request<void>('/tasks/recurrence', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }

  public async getById(recurrenceId: string): Promise<Recurrence> {
    return await this._request<Recurrence>(
      `/tasks/recurrence/${recurrenceId}`,
      {
        method: 'GET',
      },
    );
  }

  public async updateRecurrence(
    recurrenceId: string,
    payload: CreateRecurrencePayload,
  ): Promise<void> {
    if (payload?.config?.frequency !== 'week' && payload?.config?.byWeekday) {
      delete payload.config.byWeekday;
    }

    await this._request<void>(`/tasks/recurrence/${recurrenceId}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }

  public async deleteRecurrence(recurrenceId: string): Promise<void> {
    await this._request<void>(`/tasks/recurrence/${recurrenceId}`, {
      method: 'DELETE',
    });
  }

  public async getAll(
    from: DateTime,
    to: DateTime,
    userId?: string,
  ): Promise<GetAllTasksResponse[]> {
    const queryParams = new URLSearchParams();

    queryParams.append('from', from.toISO()!);
    queryParams.append('to', to.toISO()!);

    if (userId) {
      queryParams.append('user', userId);
    }

    const queryString = queryParams.toString();
    const endpoint = `/tasks/all?${queryString}`;

    return await this._request<GetAllTasksResponse[]>(endpoint, {
      method: 'GET',
    });
  }

  public async updateStatus(
    taskId: string,
    status: RecurrenceTaskStatus,
    recurrenceId: string,
    start: DateTime,
    stop: DateTime,
  ): Promise<void> {
    await this._request<void>('/tasks/status', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        taskId,
        status,
        recurrenceId,
        start,
        stop,
      }),
    });
  }

  public async approve(
    taskId: string,
    status: RecurrenceTaskStatus,
  ): Promise<void> {
    await this._request<void>('/tasks/approve', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        taskId,
        status,
      }),
    });
  }

  public async reject(taskId: string): Promise<void> {
    await this._request<void>(`/tasks/${taskId}`, {
      method: 'DELETE',
    });
  }

  public async completeRequest(
    taskId: string,
    recurrenceId: string,
  ): Promise<void> {
    await this._request<void>('/task/complete-request', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        taskId,
        recurrenceId,
      }),
    });
  }

  public async get(from?: DateTime, to?: DateTime): Promise<GetCurrentTasks[]> {
    const queryParams = new URLSearchParams();

    if (from) {
      queryParams.append('from', from.toISO()!);
    }

    if (to) {
      queryParams.append('to', to.toISO()!);
    }

    const queryString = queryParams.toString();
    const endpoint = queryString ? `/tasks?${queryString}` : '/tasks';

    return await this._request<GetCurrentTasks[]>(endpoint, {
      method: 'GET',
    });
  }

  public async getAllRecurrences(): Promise<Recurrence[]> {
    return await this._request<Recurrence[]>(`/tasks/recurrences`, {
      method: 'GET',
    });
  }

  public async getApprovalRequests(): Promise<TaskApprovalRequest[]> {
    return await this._request<TaskApprovalRequest[]>(
      '/tasks/approval-requests',
      {
        method: 'GET',
      },
    );
  }
}
